/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import moment from 'moment';
import {
  Box,
  Button,
  Chip,
  DialogContentText,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { CustomFilterV2, DialogDefaultV2, ExpandableTableV2 } from '../../../components/v2';
import {
  tableV2CellCaptionSx,
  tableV2CellContentSx,
  tableV2ChipSx,
} from '../../../components/v2/tableV2Styles';
import { codeFormatter } from 'utils';
import * as actionsEstoque from '../../../store/estoque/actions';
import Formulario from '../EstoquePedidos/Formulario';
import EstoqueVariantesDetail from './EstoqueVariantesDetail';
import { fetchEstoqueGrouped } from './fetchEstoqueGrouped';

const formatLocaisCount = (count) =>
  `${count} ${count === 1 ? 'local' : 'locais'}`;

const formatTamanhosCount = (count) => {
  if (!count) return '—';
  return `${count} ${count === 1 ? 'tamanho' : 'tamanhos'}`;
};

const EstoqueV2 = ({ exportEstoque }) => {
  const [isEntrada, setIsEntrada] = useState(false);
  const [open, setOpen] = useState(false);
  const [id, setId] = useState(null);
  const [localFilter, setLocalFilter] = useState({
    inicio: moment().subtract(2, 'months').startOf('month').valueOf(),
    fim: moment().endOf('day').valueOf(),
  });
  const [openExport, setOpenExport] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [reload, setReload] = useState(false);

  const activeLocalFilter = localFilter?.local;

  const onClose = (isSave) => {
    if (isSave) {
      setReload((current) => !current);
    }
    setOpen(false);
  };

  const loadEstoque = useCallback(
    (page, pageSize, filterData) => fetchEstoqueGrouped(page, pageSize, filterData),
    []
  );

  const columns = useMemo(
    () => [
      {
        id: 'expand',
        header: '',
        size: 48,
        cell: ({ row }) =>
          row.getCanExpand() ? (
            <IconButton
              size="small"
              aria-label={row.getIsExpanded() ? 'Recolher detalhes' : 'Expandir detalhes'}
              aria-expanded={row.getIsExpanded()}
              onClick={row.getToggleExpandedHandler()}
            >
              {row.getIsExpanded() ? (
                <ExpandMoreIcon fontSize="small" />
              ) : (
                <ChevronRightIcon fontSize="small" />
              )}
            </IconButton>
          ) : null,
      },
      {
        accessorKey: 'produtoNome',
        header: 'Produto',
        cell: ({ row }) => (
          <Box component="span" sx={tableV2CellContentSx}>
            {row.original.produtoNome}
            <br />
            ({codeFormatter(row.original.produtoCodigo)})
          </Box>
        ),
      },
      {
        accessorKey: 'quantidadeTotal',
        header: 'Qtd total',
        cell: ({ row }) => {
          const { quantidadeTotal, quantidadeNoLocal } = row.original;

          return (
            <Box sx={tableV2CellContentSx}>
              <Box component="span" sx={{ fontWeight: 600 }}>
                {quantidadeTotal}
              </Box>
              {activeLocalFilter && quantidadeNoLocal != null && (
                <Box component="div" sx={tableV2CellCaptionSx}>
                  {quantidadeNoLocal} em {activeLocalFilter}
                </Box>
              )}
            </Box>
          );
        },
      },
      {
        accessorKey: 'locaisCount',
        header: 'Locais',
        cell: ({ getValue }) => (
          <Chip
            label={formatLocaisCount(getValue() || 0)}
            size="small"
            variant="outlined"
            sx={tableV2ChipSx}
          />
        ),
      },
      {
        accessorKey: 'tamanhosCount',
        header: 'Tamanhos',
        cell: ({ getValue }) => {
          const value = getValue();
          if (!value) {
            return (
              <Box component="span" sx={tableV2CellContentSx}>
                —
              </Box>
            );
          }
          return (
            <Chip
              label={formatTamanhosCount(value)}
              size="small"
              variant="outlined"
              sx={tableV2ChipSx}
            />
          );
        },
      },
      {
        accessorKey: 'dataUltimaMovimentacao',
        header: 'Última movimentação',
        cell: ({ getValue }) => {
          const value = getValue();
          return (
            <Box component="span" sx={tableV2CellContentSx}>
              {value ? moment(value).format('DD/MM/YYYY HH:mm') : '—'}
            </Box>
          );
        },
      },
    ],
    [activeLocalFilter]
  );

  const headerActions = [
    {
      label: 'Nova Entrada',
      onClick: () => {
        setIsEntrada(true);
        setOpen(true);
      },
      startIcon: <AddIcon />,
      variant: 'contained',
    },
    {
      label: 'Nova Saída',
      onClick: () => {
        setIsEntrada(false);
        setOpen(true);
      },
      startIcon: <RemoveIcon />,
      variant: 'contained',
    },
    {
      label: 'Exportar',
      onClick: () => setOpenExport(true),
      startIcon: <FileDownloadOutlinedIcon />,
      variant: 'outlined',
    },
  ];

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ pr: 2 }}
        >
          <Typography variant="h4">Estoque</Typography>
          <Button
            variant="outlined"
            startIcon={<FilterListOutlinedIcon />}
            onClick={() => setOpenFilter(true)}
          >
            Filtros
          </Button>
        </Stack>

        <ExpandableTableV2
          fetchData={loadEstoque}
          filterData={localFilter}
          refreshKey={reload}
          headerActions={headerActions}
          singleExpand
          getRowCanExpand={(row) => (row.original.variantes?.length ?? 0) > 0}
          getRowId={(row) => String(row.id ?? row.produtoId)}
          renderSubComponent={({ row }) => (
            <EstoqueVariantesDetail
              variantes={row.original.variantes}
              localFilter={activeLocalFilter}
              produtoNome={row.original.produtoNome}
              produtoCodigo={row.original.produtoCodigo}
            />
          )}
          columns={columns}
        />

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          defaultFilter={localFilter}
          fields={[
            { field: 'useDates', md: 3 },
            { field: 'useLocal', md: 3 },
          ]}
          onFilter={(filterData) => {
            setLocalFilter(filterData);
            setOpenFilter(false);
          }}
        />

        <Formulario
          id={id}
          open={open}
          onClose={onClose}
          isEntrada={isEntrada}
        />

        <DialogDefaultV2
          open={openExport}
          handlerCancel={() => setOpenExport(false)}
          handlerConfirm={() => {
            exportEstoque(localFilter, () => {
              setOpenExport(false);
            });
          }}
          title="Confirmação"
          confirmMessage="Sim"
          cancelMessage="Não"
        >
          <DialogContentText>Deseja exportar os estoques filtrados?</DialogContentText>
        </DialogDefaultV2>
      </Stack>
    </Box>
  );
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      ...actionsEstoque,
    },
    dispatch
  );

export default connect(null, mapDispatchToProps)(EstoqueV2);
